import React, { Fragment } from 'react';
import DocumentTitle from 'react-document-title';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose, withProps } from 'recompose';
import classNames from 'classnames';
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { I18n } from '@kineticdata/react';

const PageTitleBreadcrumbs = props => {
  const LinkComponent = (props.components && props.components.Link) || Link;
  return props.breadcrumbs ? (
    <div
      role="navigation"
      aria-label="breadcrumbs"
      className="page-title__breadcrumbs"
    >
      {props.breadcrumbs.filter(b => !!b && b.label).map((b, i) => (
        <span key={`breadcrumb-${i}`}>
          {b.to ? (
            <LinkComponent to={b.to}>
              <I18n>{b.label}</I18n>
            </LinkComponent>
          ) : (
            <I18n>{b.label}</I18n>
          )}
        </span>
      ))}
    </div>
  ) : null;
};

const actionMapper = (light, components, isDropdownItem) => (a, i) => {
  const LinkComponent = (components && components.Link) || Link;
  const Tag = isDropdownItem
    ? DropdownItem
    : a.to
      ? LinkComponent
      : a.href
        ? 'a'
        : 'button';

  const actionClass = classNames(a.className, {
    'dropdown-item': a.menu,
    btn: !a.menu,
    'btn-icon-light': !a.className && !a.menu && a.icon && !a.label && light,
    'btn-icon-dark': !a.className && !a.menu && a.icon && !a.label && !light,
    'btn-outline-light':
      !a.className && !a.menu && (!a.icon || a.label) && light,
    'btn-outline-dark':
      !a.className && !a.menu && (!a.icon || a.label) && !light,
  });
  const iconClass = classNames(`fa fa-fw fa-${a.icon}`);
  const actionContent = (
    <>
      {a.icon && <span className={iconClass} />}
      {a.icon && a.label && ' '}
      {a.label && <I18n>{a.label}</I18n>}
    </>
  );
  return (
    <Fragment key={`action-${i}`}>
      {a.divider && <DropdownItem divider />}
      {a.to ? (
        <Tag
          tag={isDropdownItem ? LinkComponent : undefined}
          to={a.to}
          className={actionClass}
          aria-label={a.aria || a.label}
          title={!a.label ? a.aria : undefined}
          disabled={a.disabled}
        >
          {actionContent}
        </Tag>
      ) : a.href ? (
        <Tag
          tag={isDropdownItem ? 'a' : undefined}
          href={a.href}
          className={actionClass}
          aria-label={a.aria || a.label}
          title={!a.label ? a.aria : undefined}
          target="_blank"
          rel="noopener noreferrer"
          disabled={a.disabled}
        >
          {actionContent}
        </Tag>
      ) : typeof a.render === 'function' ? (
        a.render({ className: actionClass })
      ) : (
        <Tag
          tag={isDropdownItem ? 'button' : undefined}
          onClick={a.onClick}
          className={actionClass}
          aria-label={a.aria || a.label}
          title={!a.label ? a.aria : undefined}
          disabled={a.disabled}
        >
          {actionContent}
        </Tag>
      )}
    </Fragment>
  );
};

const Content = ({
  suptitle,
  title,
  subtitle,
  actions,
  hero = false,
  components,
}) => {
  const actionsContent = actions && (
    <div className="page-title__actions">
      {actions.filter(a => !!a && !a.menu).map(actionMapper(hero, components))}
      {actions.some(a => !!a && a.menu) && (
        <UncontrolledDropdown>
          <DropdownToggle
            tag="button"
            className={`btn btn-outline-dark`}
            aria-label="Actions"
          >
            <span>
              <I18n>Actions</I18n>
            </span>
            <span className="fa fa-angle-down fa-fw ml-2" />
          </DropdownToggle>
          <DropdownMenu right>
            {actions
              .filter(a => !!a && a.menu)
              .map(actionMapper(hero, components, true))}
          </DropdownMenu>
        </UncontrolledDropdown>
      )}
    </div>
  );
  return suptitle || title || subtitle || actions ? (
    <>
      {suptitle && (
        <div className="page-title__content">
          <small className="text-muted">
            <I18n>{suptitle}</I18n>
          </small>
          {!title && !subtitle && actionsContent}
        </div>
      )}
      {title && (
        <div className="page-title__content">
          <h1>
            <I18n>{title}</I18n>
          </h1>
          {actionsContent}
        </div>
      )}
      {subtitle && (
        <div className="page-title__content">
          <small>
            <I18n>{subtitle}</I18n>
          </small>
          {!title && actionsContent}
        </div>
      )}
      {!title &&
        !subtitle &&
        !suptitle && (
          <div className="page-title__content">{actionsContent}</div>
        )}
    </>
  ) : null;
};

const Meta = ({ meta }) =>
  meta ? (
    meta.every(m => Array.isArray(m)) ? (
      meta.map(m => <Meta meta={m} />)
    ) : (
      <div className="page-title__meta">
        <dl>
          {meta.filter(m => !!m && m.value).map((m, i) => (
            <div key={`meta-${i}`}>
              {m.label && (
                <dt>
                  <I18n>{m.label}</I18n>
                </dt>
              )}
              <dd>
                <I18n>{m.value}</I18n>
              </dd>
            </div>
          ))}
        </dl>
      </div>
    )
  ) : null;

const PageTitleContainer = props => {
  const {
    hero = false,
    heroImage,
    image = !!heroImage,
    overlayColor,
    textColor,
    center = false,
    container = false,
    withCard = false,
    className,
    children,
  } = props;
  const Container = container
    ? ({ children }) => <div className="container">{children}</div>
    : Fragment;

  // Build styles object
  const styles = {};
  if (hero) {
    if (image) {
      if (overlayColor) {
        styles.backgroundImage = `linear-gradient(${overlayColor}, ${overlayColor}), url(${heroImage})`;
      } else {
        styles.backgroundImage = `url(${heroImage})`;
      }
    } else if (overlayColor) {
      styles.backgroundColor = overlayColor;
    }
  }
  if (textColor) {
    styles.color = textColor;
  }

  return (
    <div
      className={classNames(className, {
        'page-title-heading': !hero,
        'page-title-hero': hero,
        'page-title-hero--image': hero && image && !overlayColor,
        'page-title-hero--center': hero && center,
        'page-title-hero--with-card': hero && withCard,
      })}
      style={styles}
    >
      <Container>{children}</Container>
    </div>
  );
};

const PageTitleComponent = props => {
  const {
    parts,
    breadcrumbs,
    suptitle,
    title,
    subtitle,
    actions,
    meta,
    hero,
    withCard,
    components,
    separator = '|',
    children,
  } = props;

  return (
    <>
      <I18n
        render={translate => (
          <DocumentTitle
            title={parts
              .map(p => translate(p))
              .filter(Boolean)
              .join(` ${separator} `)}
          />
        )}
      />
      {(breadcrumbs ||
        suptitle ||
        title ||
        subtitle ||
        actions ||
        meta ||
        children) && (
        <PageTitleContainer {...props}>
          <PageTitleBreadcrumbs
            breadcrumbs={breadcrumbs}
            components={components}
          />
          <Content
            suptitle={suptitle}
            title={title}
            subtitle={subtitle}
            actions={actions}
            hero={hero}
            components={components}
          />
          <Meta meta={meta} />
          {hero && withCard ? (
            <div className="page-title__card">{children}</div>
          ) : (
            children
          )}
        </PageTitleContainer>
      )}
    </>
  );
};

export const PageTitle = compose(
  connect(state => ({ space: state.app.space })),
  withProps(({ parts = [], ...props }) => ({
    parts: parts
      .concat([
        props.space && props.space.name,
        props.brand !== false && (props.brand || 'kinops'),
      ])
      .filter(Boolean),
  })),
)(PageTitleComponent);
